import { useState } from "react";
import { Link } from "react-router-dom";
import {Instagram, Youtube, Twitter, Dumbbell, ArrowUp, Loader2, Check} from 'lucide-react';
import { lumi } from "@/lib/lumi";
import toast from "react-hot-toast";

const footerHubs = [
  { label: "The Lean Physique", href: "/lean-physique" },
  { label: "Women's Strength", href: "/womens-strength" },
  { label: "Hardgainers Guide", href: "/hardgainers" },
  { label: "Muscle Calculator", href: "/calculator" },
];

const footerResources = [
  { label: "Latest Articles", href: "#articles", isAnchor: true },
  { label: "Nutrition Guides", href: "#articles", isAnchor: true },
  { label: "Workout Plans", href: "#training-hubs", isAnchor: true },
  { label: "Transformation Stories", href: "#about", isAnchor: true },
];

const socials = [
  { icon: Instagram, href: "#", label: "Instagram" },
  { icon: Youtube, href: "#", label: "YouTube" },
  { icon: Twitter, href: "#", label: "Twitter" },
];

export default function Footer() {
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  const handleAnchorClick = (href: string) => {
    const el = document.querySelector(href);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    } else {
      window.location.href = "/" + href;
    }
  };

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newsletterEmail || !newsletterEmail.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }

    setIsSubmitting(true);
    try {
      // Check if user is authenticated
      const user = lumi.auth.user;
      
      if (!user) {
        // User not logged in - prompt to create account
        toast.error("Please sign up to receive our newsletter. Use the Calculator's 'Email Results' feature to create an account!");
        setIsSubmitting(false);
        return;
      }

      // Send welcome email to the authenticated user
      await lumi.tools.email.send({
        to: user.email,
        subject: "Welcome to BuildingMassMuscle.Com Newsletter! 💪",
        fromName: "BuildingMassMuscle.Com",
        html: `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body style="margin: 0; padding: 0; font-family: Arial, sans-serif; background-color: #0F0F1A; color: #F5F5F5;">
  <table role="presentation" style="width: 100%; border-collapse: collapse;">
    <tr>
      <td align="center" style="padding: 40px 20px;">
        <table role="presentation" style="max-width: 600px; width: 100%; border-collapse: collapse; background-color: #1A1A2E; border: 1px solid rgba(255,255,255,0.05); border-radius: 16px; overflow: hidden;">
          
          <!-- Header -->
          <tr>
            <td style="background: linear-gradient(135deg, #FF6B35 0%, #FFA823 100%); padding: 40px 30px; text-align: center;">
              <h1 style="margin: 0; font-size: 32px; font-weight: bold; color: #FFFFFF; text-transform: uppercase;">
                Welcome to the Team! 💪
              </h1>
            </td>
          </tr>

          <!-- Body -->
          <tr>
            <td style="padding: 40px 30px;">
              <p style="margin: 0 0 20px 0; font-size: 16px; line-height: 1.6; color: #F5F5F5;">
                Hey <strong>${user.userName || 'there'}</strong>,
              </p>
              <p style="margin: 0 0 20px 0; font-size: 14px; line-height: 1.8; color: #9CA3AF;">
                Thanks for subscribing to the <strong style="color: #FF6B35;">BuildingMassMuscle.Com</strong> newsletter! 
                You're now part of a community committed to building strength, muscle, and better health through 
                <strong style="color: #F5F5F5;">evidence-based training and nutrition</strong>.
              </p>

              <div style="background-color: rgba(255,107,53,0.05); border: 1px solid rgba(255,107,53,0.2); border-radius: 12px; padding: 25px; margin: 30px 0;">
                <h2 style="margin: 0 0 15px 0; font-size: 18px; color: #FF6B35; text-transform: uppercase;">
                  What You'll Get Every Week:
                </h2>
                <ul style="margin: 0; padding-left: 20px; color: #9CA3AF; font-size: 14px; line-height: 1.8;">
                  <li><strong style="color: #F5F5F5;">Training Tips</strong> — New workout strategies and exercise breakdowns</li>
                  <li><strong style="color: #F5F5F5;">Nutrition Guides</strong> — Meal plans, macro tips, and supplement reviews</li>
                  <li><strong style="color: #F5F5F5;">Latest Articles</strong> — Science-backed muscle building content</li>
                  <li><strong style="color: #F5F5F5;">Exclusive Resources</strong> — Free programs and calculators</li>
                </ul>
              </div>

              <p style="margin: 0 0 20px 0; font-size: 14px; line-height: 1.8; color: #9CA3AF;">
                While you're here, check out our most popular resources:
              </p>

              <table role="presentation" style="width: 100%; border-collapse: collapse; margin: 20px 0;">
                <tr>
                  <td style="padding: 12px 0;">
                    <a href="https://www.buildingmassmuscle.com/calculator" style="color: #FF6B35; text-decoration: none; font-weight: bold; font-size: 14px;">
                      🧮 Muscle Mass Calculator →
                    </a>
                    <p style="margin: 5px 0 0 0; font-size: 12px; color: #666;">
                      Get personalized calorie and macro targets
                    </p>
                  </td>
                </tr>
                <tr>
                  <td style="padding: 12px 0;">
                    <a href="https://www.buildingmassmuscle.com/lean-physique" style="color: #FF6B35; text-decoration: none; font-weight: bold; font-size: 14px;">
                      💪 Lean Muscle Program →
                    </a>
                    <p style="margin: 5px 0 0 0; font-size: 12px; color: #666;">
                      Build muscle without excess fat gain
                    </p>
                  </td>
                </tr>
                <tr>
                  <td style="padding: 12px 0;">
                    <a href="https://www.buildingmassmuscle.com/articles" style="color: #FF6B35; text-decoration: none; font-weight: bold; font-size: 14px;">
                      📚 Training Articles →
                    </a>
                    <p style="margin: 5px 0 0 0; font-size: 12px; color: #666;">
                      Evidence-based guides and tutorials
                    </p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- CTA -->
          <tr>
            <td align="center" style="padding: 30px;">
              <table role="presentation" style="border-collapse: collapse;">
                <tr>
                  <td align="center" style="background: linear-gradient(135deg, #FF6B35 0%, #FFA823 100%); border-radius: 8px;">
                    <a href="https://www.buildingmassmuscle.com" style="display: inline-block; padding: 16px 40px; color: #FFFFFF; text-decoration: none; font-size: 14px; font-weight: bold; text-transform: uppercase;">
                      Start Building Muscle
                    </a>
                  </td>
                </tr>
              </table>
            </td>
          </tr>

          <!-- Footer -->
          <tr>
            <td style="padding: 30px; background-color: rgba(255,255,255,0.02); border-top: 1px solid rgba(255,255,255,0.05); text-align: center;">
              <p style="margin: 0; font-size: 12px; color: #666; line-height: 1.6;">
                © ${new Date().getFullYear()} BuildingMassMuscle.Com. All rights reserved.<br>
                You're receiving this because you subscribed to our newsletter.<br>
                <a href="https://www.buildingmassmuscle.com" style="color: #FF6B35; text-decoration: none;">Visit Website</a>
              </p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>
        `,
        replyTo: "support@buildingmassmuscle.com",
      });

      toast.success("✅ Welcome email sent! Check your inbox.");
      setIsSubscribed(true);
      setNewsletterEmail("");
      
      // Reset after 3 seconds
      setTimeout(() => {
        setIsSubscribed(false);
      }, 3000);
    } catch (error: any) {
      toast.error(error?.message || "Failed to subscribe. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="relative bg-[#0A0A16] border-t border-white/5">
      {/* Back to top */}
      <div className="flex justify-center -mt-5">
        <button
          onClick={scrollToTop}
          className="w-10 h-10 rounded-full bg-[#FF6B35] text-white flex items-center justify-center hover:bg-[#FFA823] transition-colors shadow-lg shadow-[#FF6B35]/20"
          aria-label="Back to top"
        >
          <ArrowUp className="w-5 h-5" />
        </button>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
          {/* Brand column */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-2.5 mb-5 group">
              <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#FF6B35] to-[#FFA823] flex items-center justify-center">
                <Dumbbell className="w-4 h-4 text-white" />
              </div>
              <div className="flex flex-col leading-none">
                <span
                  className="text-[#F5F5F5] text-base tracking-wider"
                  style={{ fontFamily: "var(--font-heading)" }}
                >
                  BUILDING MASS
                </span>
                <span className="text-[#FF6B35] text-[9px] tracking-[0.3em] font-semibold">
                  MUSCLE
                </span>
              </div>
            </Link>
            <p className="text-[#9CA3AF] text-sm leading-relaxed mb-6 max-w-xs">
              Simplifying the science of hypertrophy. Free, evidence-based
              training programs and nutrition guides for every body type.
            </p>
            <div className="flex gap-3">
              {socials.map(({ icon: Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  aria-label={label}
                  className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-[#9CA3AF] hover:text-[#FF6B35] hover:border-[#FF6B35]/30 transition-all duration-200"
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Training Hubs */}
          <div>
            <h4
              className="text-[#F5F5F5] text-sm tracking-widest mb-5"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              TRAINING HUBS
            </h4>
            <ul className="space-y-3">
              {footerHubs.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.href}
                    className="text-[#9CA3AF] hover:text-[#FF6B35] text-sm transition-colors duration-200"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4
              className="text-[#F5F5F5] text-sm tracking-widest mb-5"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              RESOURCES
            </h4>
            <ul className="space-y-3">
              {footerResources.map((link) => (
                <li key={link.label}>
                  <button
                    onClick={() => handleAnchorClick(link.href)}
                    className="text-[#9CA3AF] hover:text-[#FF6B35] text-sm transition-colors duration-200"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4
              className="text-[#F5F5F5] text-sm tracking-widest mb-5"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              GET STRONGER
            </h4>
            <p className="text-[#9CA3AF] text-sm mb-4">
              Free weekly training tips and nutrition advice straight to your
              inbox.
            </p>
            <form
              onSubmit={handleNewsletterSubmit}
              className="flex gap-2"
            >
              <input
                type="email"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                placeholder="Your email"
                disabled={isSubmitting || isSubscribed}
                className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-[#F5F5F5] placeholder-[#666] focus:outline-none focus:border-[#FF6B35]/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <button
                type="submit"
                disabled={isSubmitting || isSubscribed}
                className="bg-[#FF6B35] hover:bg-[#FFA823] text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors shrink-0 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                {isSubmitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : isSubscribed ? (
                  <Check className="w-4 h-4" />
                ) : (
                  "JOIN"
                )}
              </button>
            </form>
            {isSubscribed && (
              <p className="text-[#2E86AB] text-xs mt-2">✓ Subscribed successfully!</p>
            )}
          </div>
        </div>

        {/* Bottom bar */}
        <div className="section-divider mt-10 mb-6" />
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 text-[#666] text-xs">
          <p>&copy; {new Date().getFullYear()} BuildingMassMuscle.Com. All rights reserved.</p>
          <p>
            Evidence-based fitness advice. Always consult a physician before
            starting any exercise program.
          </p>
        </div>
      </div>
    </footer>
  );
}
